import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { PAYPAL_SDK_OPTIONS } from "@/components/PayPalPayLater";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import SocialSideBar from "@/components/SocialSideBar";
import InstallAppBanner from "@/components/InstallAppBanner";
import OfflineIndicator from "@/components/OfflineIndicator";
import PushInvite from "@/components/PushInvite";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
const Catalog = lazy(() => import("@/pages/Catalog"));
const CarDetail = lazy(() => import("@/pages/CarDetail"));
const Subscriptions = lazy(() => import("@/pages/Subscriptions"));
const RentBreve = lazy(() => import("@/pages/RentBreve"));
const RentLungo = lazy(() => import("@/pages/RentLungo"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const Account = lazy(() => import("@/pages/Account"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));

const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminRevenue = lazy(() => import("@/pages/admin/AdminRevenue"));
const AdminTransactions = lazy(() => import("@/pages/admin/AdminTransactions"));
const AdminInventory = lazy(() => import("@/pages/admin/AdminInventory"));
const AdminHomeEditor = lazy(() => import("@/pages/admin/AdminHomeEditor"));
const AdminDiscountCodes = lazy(() => import("@/pages/admin/AdminDiscountCodes"));
const AdminList = lazy(() => import("@/pages/admin/AdminList"));
const AdminCustomers = lazy(() => import("@/pages/admin/AdminCustomers"));
const AdminCalendar = lazy(() => import("@/pages/admin/AdminCalendar"));
const AdminCarCalendar = lazy(() => import("@/pages/admin/AdminCarCalendar"));
const AdminLongTerm = lazy(() => import("@/pages/admin/AdminLongTerm"));
const LongTerm = lazy(() => import("@/pages/LongTerm"));
const B2B = lazy(() => import("@/pages/B2B"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const RentToBuy = lazy(() => import("@/pages/RentToBuy"));
const AdminPlans = lazy(() => import("@/pages/admin/AdminPlans"));
const AdminPlanCreate = lazy(() => import("@/pages/admin/AdminPlanCreate"));
const AdminPlanEdit = lazy(() => import("@/pages/admin/AdminPlanEdit"));
const AdminPricingRules = lazy(() => import("@/pages/admin/AdminPricingRules"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const PublicLayout = () => {
  // Theme toggle (YouTube-style). Default = dark. Persisted in localStorage.
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("utile_theme") || "dark"; } catch { return "dark"; }
  });
  useEffect(() => {
    try { localStorage.setItem("utile_theme", theme); } catch {}
  }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  const bgClass = theme === "light" ? "public-light bg-white text-black" : "bg-[#050505] text-white";
  return (
    <div className={`${bgClass} min-h-screen`} data-theme={theme}>
      <Header onToggleTheme={toggleTheme} theme={theme} />
      <Suspense fallback={<div className="min-h-[70vh]" data-testid="page-loading" />}><Outlet /></Suspense>
      <Footer />
    </div>
  );
};

const ordersCols = [
  { key: "id", label: "ID", render: (i) => "#" + i.id.slice(0, 8) },
  { key: "user_email", label: "Cliente" },
  { key: "amount", label: "Importo", render: (i) => "€" + i.amount?.toLocaleString("it-IT") },
  { key: "payment_method", label: "Pagamento" },
  { key: "created_at", label: "Data", render: (i) => i.created_at?.slice(0, 10) },
];
const rentalsCols = [
  { key: "id", label: "ID", render: (i) => "#" + i.id.slice(0, 8) },
  { key: "user_email", label: "Cliente" },
  { key: "rental_type", label: "Tipo" },
  { key: "start_date", label: "Inizio" },
  { key: "end_date", label: "Fine" },
  { key: "amount", label: "Importo", render: (i) => "€" + i.amount },
];
const optionsCols = [
  { key: "id", label: "ID", render: (i) => "#" + i.id.slice(0, 8) },
  { key: "user_email", label: "Cliente" },
  { key: "deposit", label: "Caparra", render: (i) => "€" + i.deposit?.toLocaleString("it-IT") },
  { key: "expires_at", label: "Scade il", render: (i) => i.expires_at?.slice(0, 10) },
  { key: "payment_method", label: "Pagamento" },
];

function AppRouter() {
  const location = useLocation();
  // Emergent Google OAuth: catch #session_id=... BEFORE hitting any route
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (typeof window !== "undefined" && (location.hash?.includes("session_id=") || window.location.hash?.includes("session_id="))) {
    return <Suspense fallback={null}><AuthCallback /></Suspense>;
  }
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#0a0a0a]" data-testid="route-loading" />}>
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/catalogo" element={<Catalog />} />
        <Route path="/auto/:id" element={<CarDetail />} />
        <Route path="/auto/slug/:slug" element={<CarDetail />} />
        <Route path="/abbonamenti" element={<Subscriptions />} />
        <Route path="/noleggio/breve" element={<RentBreve />} />
        <Route path="/noleggio/breve-termine" element={<Navigate to="/noleggio/breve" replace />} />
        <Route path="/noleggio/lungo" element={<Navigate to="/noleggio/lungo-termine" replace />} />
        <Route path="/noleggio/lungo-termine" element={<LongTerm />} />
        <Route path="/noleggio/rent-to-buy" element={<RentToBuy />} />
        <Route path="/b2b" element={<B2B />} />
        <Route path="/login" element={<Login />} />
        <Route path="/registrati" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/account" element={<Account />} />
        <Route path="/pagamento/successo" element={<PaymentSuccess />} />
        <Route path="/pagamento/annullato" element={<PaymentCancel />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="transazioni" element={<AdminTransactions />} />
        <Route path="clienti" element={<AdminCustomers />} />
        <Route path="home-editor" element={<AdminHomeEditor />} />
        <Route path="sconti" element={<AdminDiscountCodes />} />
        <Route path="regole-pricing" element={<AdminPricingRules />} />

        {/* VENDITA */}
        <Route path="vendita/annunci" element={<AdminInventory filter="sale" />} />
        <Route path="vendita/ordini" element={<AdminList title="Vendita · Ordini" endpoint="/admin/orders" columns={ordersCols} />} />
        <Route path="vendita/opzioni" element={<AdminList title="Vendita · Opzioni / Caparre" endpoint="/admin/options" columns={optionsCols} />} />
        <Route path="vendita/resoconto" element={<AdminRevenue />} />
        {/* NOLEGGIO */}
        <Route path="noleggio/vetture" element={<AdminInventory filter="rent" />} />
        <Route path="noleggio/calendario" element={<AdminCalendar />} />
        <Route path="noleggio/gestione-auto" element={<AdminCarCalendar />} />
        <Route path="noleggio/lungo-termine" element={<AdminLongTerm />} />
        <Route path="noleggio/rent-to-buy" element={<AdminInventory filter="rtb" />} />
        <Route path="noleggio/lista" element={<AdminList title="Noleggio · Prenotazioni" endpoint="/admin/rentals" columns={rentalsCols} />} />
        <Route path="noleggio/abbonamenti" element={<AdminPlans />} />
        <Route path="noleggio/abbonamenti/nuovo" element={<AdminPlanCreate />} />
        <Route path="noleggio/abbonamenti/modifica/:key" element={<AdminPlanEdit />} />

        {/* Legacy redirects */}
        <Route path="inventario" element={<AdminInventory />} />
        <Route path="ordini" element={<AdminList title="Ordini" endpoint="/admin/orders" columns={ordersCols} />} />
        <Route path="noleggi" element={<AdminList title="Noleggi" endpoint="/admin/rentals" columns={rentalsCols} />} />
        <Route path="abbonamenti" element={<AdminPlans />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App bg-[#050505]">
      <HelmetProvider>
        <AuthProvider>
        <PayPalScriptProvider options={PAYPAL_SDK_OPTIONS} deferLoading>
        <BrowserRouter>
          <ScrollToTop />
          <AppRouter />
          <WhatsAppButton />
          <SocialSideBar />
          <InstallAppBanner />
          <OfflineIndicator />
          <PushInvite />
        </BrowserRouter>
        </PayPalScriptProvider>
        <Toaster theme="dark" position="top-right" />
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
